import { useQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";

import { GridInputs } from "../../../../../../../components/Functional";
import { FETCH_BANK_ACCOUNTS as fetchBankAccounts } from "../../../../../../../graphql/query/BankAccount";
import { FETCH_CURRENCIES } from "../../../../../../../graphql/query/Currency";
import { FETCH_INVOICE_TYPES } from "../../../../../../../graphql/query/InvoiceType";
import { FETCH_TAX_AREAS } from "../../../../../../../graphql/query/TaxArea";
import { useCompanies } from "../../../../../../../hooks/useCompanies";
import useUser from "../../../../../../../hooks/useUser";
import { disableForInvoice, lockedFieldConfig } from "./disableForInvoice";
import SalesPersonSection from "./SalesPersonSection";

const Information = props => {
  const { formik, submitOnBlur = false, invoice } = props;
  const { hasPermission } = useUser();
  const { companies } = useCompanies();
  const { data: { currencies = [] } = {} } = useQuery(FETCH_CURRENCIES);
  const { data: { taxAreas = [] } = {} } = useQuery(FETCH_TAX_AREAS);
  const { data: { invoiceTypes = [] } = {} } = useQuery(FETCH_INVOICE_TYPES);
  const { data: { bankAccounts = [] } = {} } = useQuery(
    fetchBankAccounts({ currency: {} }),
    { variables: { input: { companyId: formik?.values?.companyId } } },
  );

  const { bankCurrencyId, hasTransactions } = useMemo(() => {
    const selectedBankAccount = bankAccounts?.find(
      bank => bank.id === formik?.values?.bankId,
    );

    return {
      bankCurrencyId: selectedBankAccount?.currency?.id,
      hasTransactions: formik?.values?.items?.length > 0,
    };
  }, [bankAccounts, formik?.values]);

  useEffect(() => {
    if (bankCurrencyId && formik?.values?.currencyId !== bankCurrencyId) {
      formik?.setFieldValue("currencyId", bankCurrencyId);
    }
  }, [bankCurrencyId, formik]);

  const lockFormElement = (permission, extraCondition = false) =>
    disableForInvoice({ invoice, hasPermission, permission, extraCondition });

  const leftColumnInputs = [
    {
      label: "Client Name",
      allowMultilineDisplay: true,
      name: "contactId",
      type: "contactSearch",
      redirect: true,
      submitOnBlur,
      ...lockFormElement("UPDATE_INVOICE"),
    },
    {
      label: "Company",
      name: "contact.company",
      type: "text",
      disabled: true,
    },
    {
      label: "Invoiced From",
      name: "companyId",
      type: "multi-select",
      disabled: true,
      options: companies?.edges?.map(item => ({
        label: item.name,
        value: item.id,
      })),
    },
    {
      label: "Date",
      name: "date",
      type: "date",
      submitOnBlur,
      ...lockedFieldConfig,
    },
    {
      label: "Payment Due",
      name: "dueDate",
      type: "date",
      submitOnBlur,
      ...lockFormElement("UPDATE_INVOICE"),
    },
  ];

  const detailInputProps = {
    formik,
    className: "grid-cols-1 gap-4 lg:col-span-3 lg:grid-cols-3 lg:gap-16",
    inputs: [
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            label: "Type",
            name: "typeId",
            type: "multi-select",
            options: invoiceTypes?.map(item => ({
              label: item.description,
              value: item.id,
            })),
            submitOnBlur,
            ...lockFormElement("UPDATE_INVOICE"),
          },
          {
            label: "Bank",
            name: "bankId",
            type: "multi-select",
            options: bankAccounts?.map(item => {
              const number = item.number
                ? item.number
                : item.iban
                  ? item.iban?.slice(-4)
                  : "";

              return {
                label: `${item.bank} ${number} ${item.currency?.code}`,
                value: item.id,
              };
            }),
            submitOnBlur,
            ...lockFormElement("UPDATE_INVOICE", hasTransactions),
          },
          {
            label: "Currency",
            name: "currencyId",
            type: "multi-select",
            options: bankCurrencyId
              ? currencies
                  ?.filter(currency => currency.id === bankCurrencyId)
                  ?.map(item => ({
                    label: item.description,
                    value: item.id,
                  }))
              : currencies?.map(item => ({
                  label: item.description,
                  value: item.id,
                })),
            submitOnBlur,
            ...lockFormElement("UPDATE_INVOICE", hasTransactions),
          },
          {
            label: "Invoice #",
            name: "number",
            type: "text",
            disabled: true,
          },
          {
            label: "Proforma #",
            name: "proforma",
            type: "text",
            disabled: true,
          },
          {
            label: `${!invoice.invoiceSalesPersons?.length ? "You need to add sales persons before approval" : ""}`,
            name: "approved",
            options: [{ label: "Approve", value: -1 }],
            singleCheckbox: true,
            type: "checkbox",
            submitOnChange: true,
            disabled: !invoice.invoiceSalesPersons?.length,
            ...lockFormElement("UPDATE_INVOICE_SALES_NOTES"),
          },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            label: "Analysis Code",
            name: "sagecode",
            type: "text",
            submitOnBlur,
            ...lockFormElement("UPDATE_INVOICE"),
          },
          {
            label: "Client VAT",
            name: "vatno",
            type: "text",
            submitOnBlur,
            ...lockFormElement("UPDATE_INVOICE"),
          },
          {
            label: "Tax Area",
            name: "taxareaId",
            type: "select",
            options: taxAreas?.map(item => ({
              label: item.name,
              value: item.id,
            })),
            submitOnBlur,
            ...lockFormElement("UPDATE_INVOICE", hasTransactions),
          },
          {
            label: "Tax Rate",
            name: "taxRate",
            type: "text-3decimal",
            submitOnBlur,
            ...lockFormElement("UPDATE_INVOICE"),
          },
          {
            label: "Margin Scheme",
            name: "marginScheme",
            type: "toggle",
            toggleOnValue: -1,
            toggleOffValue: 0,
            submitOnBlur,
            ...lockFormElement("UPDATE_INVOICE"),
          },
          {
            label: "Margin Scheme #",
            name: "marginRef",
            type: "text",
            submitOnBlur,
            disabled: formik.values?.marginScheme != -1,
            ...lockFormElement("UPDATE_INVOICE"),
          },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            label: "Notes",
            name: "notes",
            rows: 10,
            type: "textarea",
            submitOnBlur,
            ...disableForInvoice({
              invoice,
              hasPermission,
              permission: "UPDATE_INVOICE_SALES_NOTES",
              extraCondition: !hasPermission("UPDATE_INVOICE"),
              lockWhenExported: false,
            }),
          },
          {
            label: "User",
            name: "user",
            type: "text",
            disabled: true,
          },
          {
            label: "ID",
            name: "id",
            type: "text",
            disabled: true,
          },
        ],
      },
    ],
  };

  return (
    <>
      <h5 className="font-bold">Client Information</h5>
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-4 lg:gap-16">
        <div className="grid grid-cols-1">
          <GridInputs
            formik={formik}
            inputs={leftColumnInputs}
            className="mb-6 gap-4"
          />
          <SalesPersonSection
            formik={formik}
            invoice={invoice}
            hasPermission={hasPermission}
          />
        </div>
        <GridInputs {...detailInputProps} />
      </div>
    </>
  );
};

export default Information;
